import { FC, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Image, VStack } from '@chakra-ui/react';

import laptopImage from 'assets/brand/laptop.png';
import webView from 'common/webView';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { ComputeTaxRatesDocument } from 'features/Accounting/pages/Estimation/graphql/computeTaxRates.gql';
import { TaxForecastDataDocument } from 'features/Accounting/pages/Estimation/hooks/useTaxForecastData/taxForecastData.gql';

import StepLayout from '../../layout/Step';
import { EndStepComponentProps } from '../types';
import { locales } from './locales';

const Success: FC<EndStepComponentProps> = ({ context, onBack, onFinish }) => {
  // We want the button to be in a loading state by default
  const [isFinishButtonEnabled, setIsFinishButtonEnabled] = useState(false);
  const { stepProgress } = context;
  const { isInWebView } = webView;

  const [computeTaxRates] = useMutation(ComputeTaxRatesDocument, {
    onCompleted: () => setIsFinishButtonEnabled(true),
    refetchQueries: [
      {
        query: TaxForecastDataDocument,
        variables: {
          companyProfileId: context.companyProfile.companyProfileId,
        },
      },
    ],
    variables: {
      input: {
        companyProfileId: context.companyProfile.companyProfileId,
        // @ts-expect-error Typing improvement needed, taxForecastPeriod is always defined here
        taxForecastPeriodId: context.taxForecastPeriod?.taxForecastPeriodId,
      },
    },
  });

  useEffect(() => {
    computeTaxRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        {isInWebView ? (
          <VStack gap="space-8" textAlign="center">
            <Image marginBottom="space-16" src={laptopImage} width="180px" />

            <Heading size="lg">{locales.title}</Heading>

            <Text paddingX="space-4" size="lg">
              {locales.description}
            </Text>
          </VStack>
        ) : (
          <SunshineCard.Group>
            <SunshineCard>
              <SunshineCard.Content
                alignItems="center"
                gap="space-16"
                paddingX="space-16"
                paddingY="space-24"
                textAlign="center"
              >
                <Image
                  paddingBottom="space-8"
                  src={laptopImage}
                  width="180px"
                />

                <Heading size="lg">{locales.title}</Heading>

                <Text size="lg">{locales.description}</Text>
              </SunshineCard.Content>
            </SunshineCard>

            <SunshineCard>
              <SunshineCard.Content alignItems="center" padding="space-8">
                <Button
                  isExpanded
                  isLoading={!isFinishButtonEnabled}
                  onClick={onFinish}
                >
                  {locales.finish}
                </Button>
              </SunshineCard.Content>
            </SunshineCard>
          </SunshineCard.Group>
        )}
      </StepLayout.Body>

      {isInWebView ? (
        <StepLayout.Footer stepProgress={stepProgress}>
          <StepLayout.FooterButton
            isLoading={!isFinishButtonEnabled}
            onClick={onFinish}
          >
            {locales.finish}
          </StepLayout.FooterButton>
        </StepLayout.Footer>
      ) : null}
    </StepLayout>
  );
};

export default Success;
